import React, { useEffect, useState, useRef } from "react";
import useApiTranslation from "../../hooks/useApiTranslation";
import i18n from "../../i18n";
import { useDispatch } from "react-redux";
import { addContact, updateContact } from "../../store/slices/contactsSlice";
import toast from "react-hot-toast";
import { Country, City } from "country-state-city";

const AddContactModal = ({ isOpen, onClose, contact = null, onSuccess }) => {
  const dispatch = useDispatch();
  const [formData, setFormData] = useState({
    name: contact?.name || "",
    job_title: contact?.job_title || "",
    phone_number: contact?.phone_number || "",
    email: contact?.email || "",
    address: contact?.address || "",
    city: contact?.city || "",
    country: contact?.country || "",
    zipcode: contact?.zipcode || "",
  });

  // 🔹 Country + City state
  const [countries, setCountries] = useState([]);
  const [cities, setCities] = useState([]);
  const [filteredCountries, setFilteredCountries] = useState([]);
  const [filteredCities, setFilteredCities] = useState([]);
  const [countrySearch, setCountrySearch] = useState("");
  const [citySearch, setCitySearch] = useState("");
  const [isCountryDropdownOpen, setIsCountryDropdownOpen] = useState(false);
  const [isCityDropdownOpen, setIsCityDropdownOpen] = useState(false);
  const [selectedCountryCode, setSelectedCountryCode] = useState("");

  const countryDropdownRef = useRef(null);
  const cityDropdownRef = useRef(null);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const isEditMode = !!contact;

  // 🏁 Load countries once
  useEffect(() => {
    const allCountries = Country.getAllCountries();
    setCountries(allCountries);
    setFilteredCountries(allCountries);
  }, []);

  // Update form data when contact changes (for edit mode)
  useEffect(() => {
    if (contact) {
      setFormData({
        name: contact.name || "",
        job_title: contact.job_title || "",
        phone_number: contact.phone_number || "",
        email: contact.email || "",
        address: contact.address || "",
        city: contact.city || "",
        country: contact.country || "",
        zipcode: contact.zipcode || "",
      });

      // Set country code for cities
      if (contact.country) {
        const allCountries = Country.getAllCountries();
        const found = allCountries.find((c) => c.name === contact.country);
        if (found) {
          setSelectedCountryCode(found.isoCode);
          const citiesOfCountry = City.getCitiesOfCountry(found.isoCode);
          setCities(citiesOfCountry);
          setFilteredCities(citiesOfCountry);
        }
      }
    } else {
      // Reset form when adding new contact
      setFormData({
        name: "",
        job_title: "",
        phone_number: "",
        email: "",
        address: "",
        city: "",
        country: "",
        zipcode: "",
      });
      setSelectedCountryCode("");
      setCities([]);
      setFilteredCities([]);
    }
  }, [contact]);

  // 🏙️ Load cities when country changes
  useEffect(() => {
    if (selectedCountryCode) {
      const countryCities = City.getCitiesOfCountry(selectedCountryCode);
      setCities(countryCities);
      setFilteredCities(countryCities);
    } else {
      setCities([]);
      setFilteredCities([]);
    }
  }, [selectedCountryCode]);

  // 🔍 Filter countries
  useEffect(() => {
    if (countrySearch.trim()) {
      const filtered = countries.filter((c) =>
        c.name.toLowerCase().includes(countrySearch.toLowerCase())
      );
      setFilteredCountries(filtered);
    } else {
      setFilteredCountries(countries);
    }
  }, [countrySearch, countries]);

  // 🔍 Filter cities
  useEffect(() => {
    if (citySearch.trim()) {
      const filtered = cities.filter((c) =>
        c.name.toLowerCase().includes(citySearch.toLowerCase())
      );
      setFilteredCities(filtered);
    } else {
      setFilteredCities(cities);
    }
  }, [citySearch, cities]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        countryDropdownRef.current &&
        !countryDropdownRef.current.contains(e.target)
      )
        setIsCountryDropdownOpen(false);
      if (cityDropdownRef.current && !cityDropdownRef.current.contains(e.target))
        setIsCityDropdownOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // 🔸 Form change + validation
  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: "" }));
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = "Name is required";
    if (formData.email && !/\S+@\S+\.\S+/.test(formData.email))
      newErrors.email = "Please enter a valid email";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // 🔸 Submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsSubmitting(true);
    try {
      const action = isEditMode
        ? updateContact({ id: contact.id, payload: formData })
        : addContact(formData);

      const result = await dispatch(action);

      if (result.meta.requestStatus === "fulfilled") {
        toast.success(i18n.t(isEditMode ? 'Contact updated!' : 'Contact added!', { ns: 'static' }));
        setFormData({
          name: "",
          job_title: "",
          phone_number: "",
          email: "",
          address: "",
          city: "",
          country: "",
          zipcode: "",
        });
        if (onSuccess) onSuccess();
        onClose();
      } else {
        toast.error(result.payload || i18n.t('Failed to save contact', { ns: 'static' }));
      }
    } catch (err) {
      toast.error(i18n.t('Something went wrong!', { ns: 'static' }));
    } finally {
      setIsSubmitting(false);
    }
  };

  const { triggerTranslation } = useApiTranslation(isOpen, { selector: '#add-contact-modal', enabled: isOpen, delay: 50 });

  useEffect(() => {
    if (isOpen) {
      // translate this modal content ASAP after mount
      setTimeout(() => triggerTranslation('#add-contact-modal'), 0);
    }
  }, [isOpen, triggerTranslation]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div id="add-contact-modal" className="bg-white rounded-lg shadow-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex justify-between items-center px-6 pt-6 pb-2">
          <h2 className="text-[11px] font-bold text-[#3C3C3C]">
            {isEditMode ? i18n.t('EDIT CONTACT', { ns: 'static' }) : i18n.t('ADD NEW CONTACT', { ns: 'static' })}
          </h2>
          <button
            onClick={onClose}
            disabled={isSubmitting}
            className="text-gray-400 hover:text-gray-600"
          >
            ✕
          </button>
        </div>

        <form onSubmit={handleSubmit} className="px-6 pb-6 space-y-6">
          {/* Name / Position */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-bold text-[#1A212F]">{i18n.t('Name', { ns: 'static' })}</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => handleInputChange("name", e.target.value)}
                placeholder="Michael Schott"
                className={`w-full px-3 py-2 border border-[#D6DBEA] bg-[#F8F9FB] rounded-md ${
                  errors.name ? "border-red-500" : ""
                }`}
              />
              {errors.name && (
                <p className="text-xs text-red-600">{i18n.t(errors.name, { ns: 'static' })}</p>
              )}
            </div>

            <div>
              <label className="text-sm font-bold text-[#1A212F]">
                {i18n.t('Position (optional)', { ns: 'static' })}
              </label>
              <input
                type="text"
                value={formData.job_title}
                onChange={(e) => handleInputChange("job_title", e.target.value)}
                placeholder={i18n.t('Agent', { ns: 'static' })}
                className="w-full px-3 py-2 border border-[#D6DBEA] bg-[#F8F9FB] rounded-md"
              />
            </div>
          </div>

          {/* Phone / Email */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-bold text-[#1A212F]">
                {i18n.t('Phone number', { ns: 'static' })}
              </label>
              <input
                type="tel"
                value={formData.phone_number}
                onChange={(e) =>
                  handleInputChange("phone_number", e.target.value)
                }
                placeholder={"+34 (91) 123-4567"}
                className="w-full px-3 py-2 border border-[#D6DBEA] bg-[#F8F9FB] rounded-md"
              />
            </div>
            <div>
              <label className="text-sm font-bold text-[#1A212F]">
                {i18n.t('Email address', { ns: 'static' })}
              </label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => handleInputChange("email", e.target.value)}
                placeholder={"michael@chelseafc.com"}
                className={`w-full px-3 py-2 border border-[#D6DBEA] bg-[#F8F9FB] rounded-md ${
                  errors.email ? "border-red-500" : ""
                }`}
              />
              {errors.email && (
                <p className="text-xs text-red-600">{i18n.t(errors.email, { ns: 'static' })}</p>
              )}
            </div>
          </div>

          {/* Address */}
          <div>
            <label className="text-sm font-bold text-[#1A212F]">{i18n.t('Address', { ns: 'static' })}</label>
            <input
              type="text"
              value={formData.address}
              onChange={(e) => handleInputChange("address", e.target.value)}
              placeholder={i18n.t('Full address', { ns: 'static' })}
              className="w-full px-3 py-2 border border-[#D6DBEA] bg-[#F8F9FB] rounded-md"
            />
          </div>

          {/* Country / City / Zip */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Country */}
            <div className="relative" ref={countryDropdownRef}>
              <label className="text-sm font-bold text-[#1A212F]">{i18n.t('Country', { ns: 'static' })}</label>
              <input
                type="text"
                value={isCountryDropdownOpen ? countrySearch : formData.country}
                onChange={(e) => setCountrySearch(e.target.value)}
                onFocus={() => setIsCountryDropdownOpen(true)}
                placeholder={i18n.t('Country', { ns: 'static' })}
                className="w-full px-3 py-2 border border-[#D6DBEA] bg-[#F8F9FB] rounded-md"
              />
              {isCountryDropdownOpen && (
                <div className="absolute bottom-full mb-1 z-50 bg-white border border-gray-200 rounded-md shadow-md w-full max-h-60 overflow-y-auto overflow-x-hidden">
                  {filteredCountries.map((c) => (
                    <div
                      key={c.isoCode}
                      onClick={() => {
                        handleInputChange("country", c.name);
                        setSelectedCountryCode(c.isoCode);
                        // Clear city when country changes
                        handleInputChange("city", "");
                        setIsCountryDropdownOpen(false);
                      }}
                      className="px-3 py-2 flex items-start cursor-pointer hover:bg-gray-100"
                    >
                      <img
                        src={`https://flagcdn.com/24x18/${c.isoCode.toLowerCase()}.png`}
                        alt={c.name}
                        className="mr-2 w-5 h-4 object-cover rounded-sm flex-shrink-0 mt-0.5"
                      />
                      <span className="break-words">{c.name}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* City */}
            <div className="relative" ref={cityDropdownRef}>
              <label className="text-sm font-bold text-[#1A212F]">{i18n.t('City', { ns: 'static' })}</label>
              <input
                type="text"
                value={isCityDropdownOpen ? citySearch : formData.city}
                onChange={(e) => setCitySearch(e.target.value)}
                onFocus={() => setIsCityDropdownOpen(true)}
                placeholder={i18n.t('City', { ns: 'static' })}
                className="w-full px-3 py-2 border border-[#D6DBEA] bg-[#F8F9FB] rounded-md"
              />
              {isCityDropdownOpen && (
                <div className={`absolute z-50 bg-white border border-gray-200 rounded-md shadow-md w-full max-h-60 overflow-y-auto overflow-x-hidden ${
                  (!selectedCountryCode || filteredCities.length === 0) ? 'top-full mt-1' : 'bottom-full mb-1'
                }`}>
                  {!selectedCountryCode ? (
                    <div className="px-3 py-2 text-sm text-gray-500 text-center">
                      {i18n.t('Select country first', { ns: 'static' })}
                    </div>
                  ) : filteredCities.length === 0 ? (
                    <div className="px-3 py-2 text-sm text-gray-500 text-center">
                      {i18n.t('No cities found', { ns: 'static' })}
                    </div>
                  ) : (
                    filteredCities.map((city, idx) => (
                      <div
                        key={idx}
                        onClick={() => {
                          handleInputChange("city", city.name);
                          setIsCityDropdownOpen(false);
                        }}
                        className="px-3 py-2 cursor-pointer hover:bg-gray-100 break-words"
                      >
                        {city.name}
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>

            {/* Zipcode */}
            <div>
              <label className="text-sm font-bold text-[#1A212F]">
                {i18n.t('Zipcode', { ns: 'static' })}
              </label>
              <input
                type="text"
                value={formData.zipcode}
                onChange={(e) => handleInputChange("zipcode", e.target.value)}
                placeholder={i18n.t('Zipcode', { ns: 'static' })}
                className="w-full px-3 py-2 border border-[#D6DBEA] bg-[#F8F9FB] rounded-md"
              />
            </div>
          </div>

          {/* Buttons */}
          <div className="flex justify-start gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2 border border-gray-300 bg-white rounded-md hover:bg-gray-50"
            >
              {i18n.t('Cancel', { ns: 'static' })}
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            >
              {isSubmitting ? i18n.t('Saving...', { ns: 'static' }) : i18n.t('Confirm', { ns: 'static' })}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddContactModal;
